import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  HandRaisedIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ScaleIcon,
  ClockIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  LightBulbIcon,
  ChartBarIcon,
  CogIcon,
} from '@heroicons/react/24/outline';

const GovernanceInfoPage: React.FC = () => {
  const governanceProcess = [
    {
      step: 1,
      title: 'Discussion',
      description: 'Community members discuss ideas on the forum before formal proposal submission.',
      duration: 'Ongoing',
    },
    {
      step: 2,
      title: 'Proposal Draft',
      description: 'Idea is formalized into a governance proposal with specific implementation details.',
      duration: '3-7 days',
    },
    {
      step: 3,
      title: 'Temperature Check',
      description: 'Informal vote to gauge community interest before on-chain proposal.',
      duration: '3 days',
    },
    {
      step: 4,
      title: 'On-Chain Proposal',
      description: 'Proposal is submitted on-chain by a qualified proposer with sufficient voting power.',
      duration: '1 day',
    },
    {
      step: 5,
      title: 'Voting Period',
      description: 'Token holders vote For, Against, or Abstain on the proposal.',
      duration: '5 days',
    },
    {
      step: 6,
      title: 'Timelock & Execution',
      description: 'Passed proposals enter a timelock period before automatic execution.',
      duration: '2 days',
    },
  ];

  const proposalTypes = [
    {
      icon: BanknotesIcon,
      title: 'Treasury Proposals',
      description: 'Allocate funds from the DAO treasury for development, marketing, partnerships, or grants.',
      examples: ['Fund new feature development', 'Marketing campaign budget', 'Security audit funding'],
    },
    {
      icon: CogIcon,
      title: 'Parameter Changes',
      description: 'Modify platform parameters like fees, staking rewards, auction settings, or tier requirements.',
      examples: ['Adjust platform fee percentage', 'Change staking tier thresholds', 'Modify auction durations'],
    },
    {
      icon: DocumentTextIcon,
      title: 'Protocol Upgrades',
      description: 'Implement smart contract upgrades or add new features to the platform.',
      examples: ['New auction type implementation', 'Cross-chain integration', 'Smart contract optimization'],
    },
    {
      icon: UserGroupIcon,
      title: 'Governance Changes',
      description: 'Modify the governance process itself, including voting periods or quorum requirements.',
      examples: ['Adjust quorum thresholds', 'Change voting periods', 'Update proposal requirements'],
    },
  ];

  const votingPower = [
    { tier: 'Basic', stake: '< 1,000 NDH', power: '1x', description: 'Standard voting weight' },
    { tier: 'Bronze', stake: '1,000+ NDH', power: '1.1x', description: '10% voting boost' },
    { tier: 'Silver', stake: '10,000+ NDH', power: '1.25x', description: '25% voting boost' },
    { tier: 'Gold', stake: '50,000+ NDH', power: '1.5x', description: '50% voting boost' },
    { tier: 'Platinum', stake: '250,000+ NDH', power: '2x', description: 'Double voting power' },
  ];

  const stats = [
    { label: 'Total Proposals', value: '145' },
    { label: 'Passed Proposals', value: '112' },
    { label: 'Active Voters', value: '8,500+' },
    { label: 'Treasury Value', value: '$15M+' },
  ];

  const currentProposals = [
    {
      id: 'TIP-142',
      title: 'Reduce Platform Fee from 2.5% to 2%',
      status: 'Voting',
      forVotes: '2.4M NDH',
      againstVotes: '800K NDH',
      endTime: '2 days',
    },
    {
      id: 'TIP-141',
      title: 'Add Support for Solana NFTs',
      status: 'Passed',
      forVotes: '3.1M NDH',
      againstVotes: '450K NDH',
      endTime: 'Executed',
    },
    {
      id: 'TIP-140',
      title: 'Allocate 500K NDH for Security Audit',
      status: 'Passed',
      forVotes: '2.8M NDH',
      againstVotes: '120K NDH',
      endTime: 'Executed',
    },
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-purple-500/20 rounded-2xl">
                <ShieldCheckIcon className="w-12 h-12 text-purple-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              DAO <span className="text-purple-400">Governance</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              TEMPLUM is governed by its community. As an NDH token holder, you have the power to propose and vote on changes that shape the platform's future.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/dashboard/governance"
                className="inline-flex items-center gap-2 px-8 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl transition-colors"
              >
                View Active Proposals
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
              <a
                href="#process"
                className="px-8 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
              >
                Learn the Process
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-dark-900/50 border-y border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-purple-400 mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What is DAO Governance */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-purple-400 font-semibold mb-2 block">Decentralized Decision Making</span>
                <h2 className="text-3xl font-bold text-white mb-6">What is DAO Governance?</h2>
                <p className="text-gray-300 mb-4">
                  A DAO (Decentralized Autonomous Organization) is a governance structure where decisions are made collectively by token holders, not a central authority.
                </p>
                <p className="text-gray-300 mb-4">
                  In TEMPLUM, NDH token holders vote on proposals that affect the platform—from fee structures and feature additions to treasury allocations and protocol upgrades.
                </p>
                <p className="text-gray-300">
                  Every vote is recorded on-chain, ensuring complete transparency and immutability. The community truly owns and operates the platform.
                </p>
              </div>
              <div className="space-y-4">
                <div className="bg-dark-800/50 rounded-xl p-4 border border-dark-700 flex items-start gap-4">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <HandRaisedIcon className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">One Token, One Vote</h3>
                    <p className="text-gray-400 text-sm">Your voting power is proportional to your NDH holdings (with tier multipliers).</p>
                  </div>
                </div>
                <div className="bg-dark-800/50 rounded-xl p-4 border border-dark-700 flex items-start gap-4">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <ScaleIcon className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Transparent Process</h3>
                    <p className="text-gray-400 text-sm">All proposals, votes, and executions are recorded on the blockchain.</p>
                  </div>
                </div>
                <div className="bg-dark-800/50 rounded-xl p-4 border border-dark-700 flex items-start gap-4">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <ClockIcon className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Timelock Security</h3>
                    <p className="text-gray-400 text-sm">Passed proposals have a delay before execution, allowing review and preparation.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Governance Process */}
      <section id="process" className="py-16 bg-dark-900/30 scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Governance Process</h2>
              <p className="text-gray-400">How proposals move from idea to implementation</p>
            </div>
            <div className="space-y-6">
              {governanceProcess.map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex gap-6"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center text-purple-400 font-bold">
                      {step.step}
                    </div>
                    {idx < governanceProcess.length - 1 && (
                      <div className="w-0.5 h-full bg-dark-700 mt-2" />
                    )}
                  </div>
                  <div className="flex-1 pb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-white">{step.title}</h3>
                      <span className="px-2 py-1 bg-dark-700 text-gray-400 text-xs rounded">
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-gray-400">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Proposal Types */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Types of Proposals</h2>
              <p className="text-gray-400">Different categories of governance decisions</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {proposalTypes.map((type, idx) => {
                const Icon = type.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
                  >
                    <Icon className="w-10 h-10 text-purple-400 mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">{type.title}</h3>
                    <p className="text-gray-400 mb-4">{type.description}</p>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500">Examples:</p>
                      {type.examples.map((example, eidx) => (
                        <p key={eidx} className="text-sm text-gray-400 flex items-center gap-2">
                          <CheckCircleIcon className="w-4 h-4 text-green-400" />
                          {example}
                        </p>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Voting Power */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Voting Power</h2>
              <p className="text-gray-400">Higher staking tiers get increased voting weight</p>
            </div>
            <div className="bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-700">
                    <th className="text-left py-4 px-6 text-gray-400 font-medium">Tier</th>
                    <th className="text-left py-4 px-6 text-gray-400 font-medium">Stake Required</th>
                    <th className="text-center py-4 px-6 text-gray-400 font-medium">Power Multiplier</th>
                    <th className="text-left py-4 px-6 text-gray-400 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {votingPower.map((tier, idx) => (
                    <tr key={idx} className="border-b border-dark-700 last:border-0">
                      <td className="py-4 px-6 text-white font-medium">{tier.tier}</td>
                      <td className="py-4 px-6 text-gray-400">{tier.stake}</td>
                      <td className="py-4 px-6 text-center">
                        <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded font-bold">
                          {tier.power}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-400">{tier.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Proposals */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Recent Proposals</h2>
              <p className="text-gray-400">See what the community is voting on</p>
            </div>
            <div className="space-y-4">
              {currentProposals.map((proposal, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <span className="text-primary-400 font-mono text-sm">{proposal.id}</span>
                      <h3 className="text-lg font-semibold text-white">{proposal.title}</h3>
                    </div>
                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                      proposal.status === 'Voting' ? 'bg-yellow-500/20 text-yellow-400' :
                      proposal.status === 'Passed' ? 'bg-green-500/20 text-green-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {proposal.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <span className="text-gray-500">For: </span>
                      <span className="text-green-400">{proposal.forVotes}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Against: </span>
                      <span className="text-red-400">{proposal.againstVotes}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Time: </span>
                      <span className="text-gray-400">{proposal.endTime}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/dashboard/governance"
                className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300"
              >
                View All Proposals <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Contract */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <h3 className="text-xl font-semibold text-white mb-4">Governance Contract</h3>
              <pre className="bg-dark-900 rounded-lg p-4 text-xs text-gray-400 overflow-x-auto">
{`// SPDX-License-Identifier: MIT
pragma solidity ^0.8.19;

import "@openzeppelin/contracts/governance/Governor.sol";
import "@openzeppelin/contracts/governance/extensions/GovernorVotes.sol";
import "@openzeppelin/contracts/governance/extensions/GovernorTimelockControl.sol";

contract TemplumGovernor is Governor, GovernorVotes, GovernorTimelockControl {
    uint256 public constant VOTING_DELAY = 1 days;
    uint256 public constant VOTING_PERIOD = 5 days;
    uint256 public constant PROPOSAL_THRESHOLD = 100_000 * 1e18;
    uint256 public constant QUORUM_PERCENTAGE = 4; // 4% of total supply

    function propose(
        address[] memory targets,
        uint256[] memory values,
        bytes[] memory calldatas,
        string memory description
    ) public override returns (uint256) {
        require(
            getVotes(msg.sender, block.number - 1) >= PROPOSAL_THRESHOLD,
            "Insufficient voting power"
        );
        return super.propose(targets, values, calldatas, description);
    }

    function quorum(uint256 blockNumber) public view override returns (uint256) {
        return (token.totalSupply() * QUORUM_PERCENTAGE) / 100;
    }
    // ... additional governance functions
}`}
              </pre>
              <a
                href="https://github.com/templum-dao/contracts"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-purple-400 text-sm mt-4 hover:underline"
              >
                View full contract on GitHub <ArrowRightIcon className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl p-8 md:p-12 border border-purple-500/30 text-center">
              <LightBulbIcon className="w-12 h-12 text-purple-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-4">
                Have an Idea for TEMPLUM?
              </h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Join the discussion on our forum, draft a proposal, and shape the future of decentralized auctions.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/dashboard/governance"
                  className="px-8 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl transition-colors"
                >
                  Go to Governance Dashboard
                </Link>
                <a
                  href="https://forum.templum.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
                >
                  Join Forum Discussion
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GovernanceInfoPage;
